import { Link } from "react-router-dom";
import { MEETING_REASON_LABEL as REASON_LABEL } from "@/lib/meeting-labels";
import type { ClinicianPatientMeeting } from "@care-by-ella/contracts";
import {
  COMPLETION_ITEM_LABEL,
  COMPLETION_OUTCOME_LABEL,
} from "@care-by-ella/contracts";
import { NEXT_STEP_LABELS } from "@/lib/meeting-next-step-labels";
import { patientMeetingHref } from "@/lib/patient-links";
import { useViewPath } from "./SupportView";

// TES-506 — the patient's care meetings, and what was written in them.
//
// The walkthrough 2026-09-04, two findings that are the same thing:
//   "Vårdmöte under patienten som en flik"
//   "ANTEckningarna går inte att hitta i efterhand (inte heller recept)"
//
// The note and the prescription have always lived on the meeting row. What
// was missing was a way back to them: the meeting screen is reached only via
// the meeting's id, and the list of meetings existed only in the doctor's own
// meeting list, sorted by time and not by patient. This is that way — newest
// first, with the assessment and the prescription written out, not behind
// yet another click.

const STATUS_LABEL: Record<string, string> = {
  scheduled: "Bokat",
  in_progress: "Pågår",
  completed: "Genomfört",
  no_show: "Uteblev",
  cancelled: "Avbokat",
};

function fmt(iso: string): string {
  return new Date(iso).toLocaleString("sv-SE", {
    dateStyle: "medium",
    timeStyle: "short",
  });
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col gap-[2px]">
      <span className="text-xs uppercase tracking-wide text-textSecondary">
        {label}
      </span>
      {/* Free text from the journal: the line breaks the doctor wrote are
          part of the note and must survive rendering. */}
      <p className="whitespace-pre-wrap text-sm text-textPrimary">{value}</p>
    </div>
  );
}

export function PatientMeetings({
  patientId,
  meetings,
}: {
  patientId: string;
  meetings: ClinicianPatientMeeting[];
}) {
  const viewPath = useViewPath();
  if (meetings.length === 0) {
    return (
      <section className="rounded-md border border-surfaceMuted bg-surface p-md text-sm text-textSecondary">
        Inga vårdmöten bokade än.
      </section>
    );
  }

  return (
    <div className="flex flex-col gap-sm">
      {meetings.map((m) => {
        const doc = m.documentation;
        return (
          <section
            key={m.id}
            className="rounded-md border border-surfaceMuted bg-surface p-md"
          >
            <div className="flex flex-wrap items-baseline gap-sm">
              <span className="font-medium">{fmt(m.scheduledAt)}</span>
              <span className="text-sm text-textSecondary">
                {REASON_LABEL[m.reason] ?? m.reason} ·{" "}
                {STATUS_LABEL[m.status] ?? m.status}
                {m.doctorDisplayName ? ` · ${m.doctorDisplayName}` : ""}
              </span>
              <Link
                to={viewPath(patientMeetingHref(patientId, m.id))}
                className="ml-auto text-sm text-brand hover:underline"
              >
                Öppna mötet →
              </Link>
            </div>

            {doc ? (
              <div className="mt-md flex flex-col gap-sm border-t border-surfaceMuted pt-md">
                <p className="text-xs text-textSecondary">
                  Dokumenterat {doc.documentedAt ? fmt(doc.documentedAt) : "—"}
                  {doc.documentedByDisplayName
                    ? ` av ${doc.documentedByDisplayName}`
                    : ""}
                </p>
                {/* TES-630 — the journal note for the meeting is written in
                    Webdoc. This is the draft and the care team's notes, and
                    the labels say so: a colleague reading the history must
                    know she is not looking at the journal. */}
                {doc.assessment ? (
                  <Field
                    label="Utkast till journalanteckning"
                    value={doc.assessment}
                  />
                ) : null}
                {doc.prescription ? (
                  <Field label="Recept" value={doc.prescription} />
                ) : null}
                {doc.treatmentPlanNote ? (
                  <Field
                    label="Behandlingsplan"
                    value={doc.treatmentPlanNote}
                  />
                ) : null}
                {/* TES-434 B — the patient's own answer to
                    "Hur var ditt vårdmöte?", visible to the responsible
                    doctor. */}
                {m.patientRating !== null ? (
                  <p className="text-sm text-textSecondary">
                    Patientens betyg: {m.patientRating}/5
                  </p>
                ) : null}
                {/* TES-537 — what the doctor chose; older meetings only have
                    the derived flags. TES-950 — a meeting documented with the
                    completion picker shows its outcome and items instead of
                    the package its `nextStep` was derived as. */}
                <p className="text-sm text-textSecondary">
                  {doc.completion
                    ? `${COMPLETION_OUTCOME_LABEL[doc.completion.outcome]}${
                        doc.completion.items.length > 0
                          ? `: ${doc.completion.items
                              .map((i) => COMPLETION_ITEM_LABEL[i])
                              .join(", ")}`
                          : ""
                      }`
                    : doc.nextStep
                      ? `Nästa steg: ${NEXT_STEP_LABELS[doc.nextStep].title}`
                      : `${doc.labRequired ? "Provtagning krävs" : "Inga prover krävs"} · ${
                          doc.treatmentProceed
                            ? "Fortsätter med behandling"
                            : "Fortsätter inte med behandling"
                        }`}
                  {/* TES-997 (Ö-259) — "Muntlig abonnemangsinformation" is
                      removed from doctor admin entirely, history included. */}
                </p>
                {/* A documentation without a single free-text field is a
                    valid decision since the assessment became optional — say
                    so, instead of showing an empty card. */}
                {!doc.assessment &&
                !doc.prescription &&
                !doc.treatmentPlanNote ? (
                  <p className="text-sm text-textSecondary">
                    Ingen fritext skrevs här — besluten ovan är allt som
                    sparades hos oss.
                  </p>
                ) : null}
              </div>
            ) : (
              <p className="mt-sm text-sm text-textSecondary">
                Inte dokumenterat än.
              </p>
            )}
          </section>
        );
      })}
    </div>
  );
}
